/**
 * Zustand store for transient app UI state (currently: toast notifications).
 */
import { create } from 'zustand';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
}

interface AppState {
  toasts: Toast[];
  addToast: (type: Toast['type'], title: string, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

export const useAppStore = create<AppState>()((set, get) => ({
  toasts: [],

  addToast: (type, title, message, duration = 4500) => {
    const id = Math.random().toString(36).substring(2, 9);
    set((state) => ({ toasts: [...state.toasts, { id, type, title, message }] }));
    if (duration > 0) {
      setTimeout(() => get().removeToast(id), duration);
    }
  },

  removeToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
}));

// Convenience wrappers for firing toasts from anywhere
export const useToast = () => {
  const addToast = useAppStore((state) => state.addToast);

  return {
    success: (title: string, message: string) => addToast('success', title, message),
    error: (title: string, message: string) => addToast('error', title, message),
    warning: (title: string, message: string) => addToast('warning', title, message),
    info: (title: string, message: string) => addToast('info', title, message),
  };
};
